import { useState } from 'react';
import { Segmented, Stat } from '../components/data';
import { ErrorBanner, Field, Loading, PageHead } from '../components/ui';
import { downloadCsv, fileStamp } from '../lib/download';
import { formatNumber, num, pct } from '../lib/format';
import { useHeldResource } from '../lib/hooks';

/** One line of the statement. `values` is null for a gap: a line a P&L
    carries that nothing in the app records. */
interface Line {
  key: string;
  label: string;
  values: number[] | null;
  ytd: number | null;
  source: string;
}
interface PctLine {
  key: string;
  label: string;
  values: (number | null)[];
  ytd: number | null;
}
interface Payload {
  year: number;
  months: string[];
  partialMonth: string | null;
  fetchedUtc: string;
  revenue: Line[];
  totalRevenue: Line;
  cogs: Line[];
  totalCogs: Line;
  grossProfit: Line;
  grossMargin: PctLine;
  opex: Line[];
  totalOpex: Line;
  netProfit: Line;
  netMargin: PctLine;
  memo: Line[];
  counts: { billingItems: number; timeEntries: number; directHours: number; internalHours: number; expenseItems: number; invoices: number };
  warnings: string[];
}

const money = (v: number | null | undefined) => (v === null || v === undefined ? '—' : formatNumber(v, 'currency'));
const monthLabel = (ym: string) => new Date(`${ym}-01T00:00:00`).toLocaleDateString([], { month: 'short' });

/**
 * Reports › PNL — a makeshift profit and loss for the year chosen, by month
 * with a year-to-date column (routes/reportsPnl.ts). Built from Autotask's
 * billing, timesheets and expense reports, so it is only as complete as they
 * are: the lines nothing in the app records are shown as gaps, and every line
 * names its source underneath.
 */
export function PnlReportPage() {
  const thisYear = new Date().getFullYear();
  const [year, setYear] = useState(thisYear);
  const data = useHeldResource<Payload>(`/reports/pnl?year=${year}`);
  const d = data.data;

  const exportCsv = () => {
    if (!d) return;
    const row = (l: Line | PctLine) => [l.label, ...(l.values ?? d.months.map(() => 'no source')), l.ytd ?? ''];
    downloadCsv(`pnl_${d.year}_${fileStamp()}.csv`, [
      ['Line', ...d.months, 'YTD'],
      ['Revenue'],
      ...d.revenue.map(row),
      row(d.totalRevenue),
      ['Cost of sales'],
      ...d.cogs.map(row),
      row(d.totalCogs),
      row(d.grossProfit),
      row(d.grossMargin),
      ['Operating expenses'],
      ...d.opex.map(row),
      row(d.totalOpex),
      row(d.netProfit),
      row(d.netMargin),
      ['Memo'],
      ...d.memo.map(row),
    ]);
  };

  return (
    <>
      <PageHead
        title="PNL"
        lede="A makeshift profit and loss for the year, month by month, assembled from what Autotask holds — billing, timesheets and expense reports. Not the books: lines with no source in the app are shown as gaps, not guessed."
      />

      <section className="card mb-4">
        <div className="card-body">
          <div className="row wrap" style={{ alignItems: 'flex-end', gap: 'var(--space-3)' }}>
            <Field label="Year">
              <Segmented
                label="Year"
                value={String(year)}
                onChange={(v) => setYear(Number(v))}
                options={[thisYear, thisYear - 1, thisYear - 2].map((y) => ({ id: String(y), label: String(y) }))}
              />
            </Field>
            <button type="button" className="btn btn-secondary btn-sm" disabled={!d} onClick={exportCsv}>
              Export CSV
            </button>
            <button type="button" className="btn btn-ghost btn-sm" disabled={data.refreshing} onClick={data.refresh}>
              {data.refreshing ? 'Refreshing…' : 'Refresh from Autotask'}
            </button>
          </div>
          <p className="xs muted mt-2">
            {d?.partialMonth && `${monthLabel(d.partialMonth)} is month to date. `}
            A year of billing and timesheets takes a minute or so to read.
            {data.fetchedAt &&
              ` As of ${new Date(data.fetchedAt).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}.`}
          </p>
        </div>
      </section>

      {data.error && <ErrorBanner message={data.error} />}
      {data.loading && !d && <Loading label="Reading a year of billing and timesheets from Autotask" />}

      {d && (
        <>
          <div className="grid grid-4 mb-4">
            <Stat label="Revenue YTD" value={money(d.totalRevenue.ytd)} foot={`${num(d.counts.billingItems)} billing items`} />
            <Stat label="Gross profit YTD" value={money(d.grossProfit.ytd)} foot={`${pct(d.grossMargin.ytd)} gross margin`} />
            <Stat label="Recorded expenses YTD" value={money(d.totalOpex.ytd)} foot="internal time and staff expenses only" />
            <Stat label="Net profit YTD" value={money(d.netProfit.ytd)} foot="before the expenses the app cannot see" />
          </div>

          {d.warnings.length > 0 && (
            <section className="card mb-4">
              <div className="card-body">
                {d.warnings.map((w) => (
                  <p key={w} className="small" style={{ margin: 0 }}>
                    ⚠ {w}
                  </p>
                ))}
              </div>
            </section>
          )}

          <section className="card mb-4">
            <div className="table-wrap">
              <table className="table pnl-table">
                <thead>
                  <tr>
                    <th>{d.year}</th>
                    {d.months.map((mo) => (
                      <th key={mo} className="num">
                        {monthLabel(mo)}
                        {mo === d.partialMonth && '*'}
                      </th>
                    ))}
                    <th className="num pnl-ytd">YTD</th>
                  </tr>
                </thead>
                <tbody>
                  <Heading title="Revenue" span={d.months.length + 2} />
                  {d.revenue.map((l) => (
                    <MoneyRow key={l.key} line={l} months={d.months.length} />
                  ))}
                  <MoneyRow line={d.totalRevenue} months={d.months.length} tone="total" />
                  <Heading title="Cost of sales" span={d.months.length + 2} />
                  {d.cogs.map((l) => (
                    <MoneyRow key={l.key} line={l} months={d.months.length} />
                  ))}
                  <MoneyRow line={d.totalCogs} months={d.months.length} tone="total" />
                  <MoneyRow line={d.grossProfit} months={d.months.length} tone="result" />
                  <PctRow line={d.grossMargin} />
                  <Heading title="Operating expenses" span={d.months.length + 2} />
                  {d.opex.map((l) => (
                    <MoneyRow key={l.key} line={l} months={d.months.length} />
                  ))}
                  <MoneyRow line={d.totalOpex} months={d.months.length} tone="total" />
                  <MoneyRow line={d.netProfit} months={d.months.length} tone="result" />
                  <PctRow line={d.netMargin} />
                  <Heading title="Memo — not in the totals" span={d.months.length + 2} />
                  {d.memo.map((l) => (
                    <MoneyRow key={l.key} line={l} months={d.months.length} />
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <section className="card">
            <div className="card-body">
              <h2 style={{ marginTop: 0 }}>
                Where each line comes from
              </h2>
              <p className="small muted">
                This is makeshift: there is no general ledger in the app, so the statement is pieced together from Autotask. Labor is costed at each
                technician’s internal hourly cost in Autotask, for the hours they logged — {num(d.counts.directHours, 1)} hours on client work and{' '}
                {num(d.counts.internalHours, 1)} hours internal this year. Lines marked “no source” are not zero; they are simply not recorded anywhere
                the app can read, so net profit here is higher than the real one.
              </p>
              <dl className="pnl-sources">
                {[...d.revenue, ...d.cogs, ...d.opex, ...d.memo].map((l) => (
                  <div key={l.key}>
                    <dt>
                      {l.label}
                      {l.values === null && <span className="badge badge-neutral" style={{ marginLeft: 'var(--space-2)' }}>no source</span>}
                    </dt>
                    <dd className="small muted">{l.source}</dd>
                  </div>
                ))}
              </dl>
            </div>
          </section>
        </>
      )}
    </>
  );
}

function Heading({ title, span }: { title: string; span: number }) {
  return (
    <tr className="pnl-heading">
      <td colSpan={span}>{title}</td>
    </tr>
  );
}

function MoneyRow({ line, months, tone }: { line: Line; months: number; tone?: 'total' | 'result' }) {
  return (
    <tr className={tone ? `pnl-${tone}` : undefined} title={line.source}>
      <td className="pnl-label">{line.label}</td>
      {line.values === null ? (
        <td colSpan={months + 1} className="muted pnl-gap">
          no source in the app
        </td>
      ) : (
        <>
          {line.values.map((v, i) => (
            <td key={i} className={`num${v < 0 ? ' pnl-neg' : ''}`}>
              {money(v)}
            </td>
          ))}
          <td className={`num pnl-ytd${line.ytd !== null && line.ytd < 0 ? ' pnl-neg' : ''}`}>{money(line.ytd)}</td>
        </>
      )}
    </tr>
  );
}

function PctRow({ line }: { line: PctLine }) {
  return (
    <tr className="pnl-pct">
      <td className="pnl-label">{line.label}</td>
      {line.values.map((v, i) => (
        <td key={i} className="num">
          {pct(v)}
        </td>
      ))}
      <td className="num pnl-ytd">{pct(line.ytd)}</td>
    </tr>
  );
}
